import { z } from 'zod';
/**
 * Vì sao lưu trữ một dự án — APM-PARCH.
 *
 * Danh sách **đóng**, không phải ô nhập tự do: ba lý do này là ba câu chuyện
 * khác nhau khi đọc lại lịch sử ("sao dự án này biến mất?"), và một ô tự do sẽ
 * cho ra ba trăm cách viết cùng một ý.
 *
 * - `created_by_mistake`: tạo nhầm — trùng, sai tên, thử nghiệm.
 * - `stopped`: dừng giữa chừng — không huỷ, nhưng không làm tiếp.
 * - `cancelled`: bị huỷ — quyết định chính thức không làm.
 */
export const ProjectArchiveReason = z.enum(['created_by_mistake', 'stopped', 'cancelled']);
/**
 * Làm gì với nguồn đồng bộ khi lưu trữ — APM-PARCH-c.
 *
 * - `keep`: để nguyên. Đồng bộ vốn tự ngừng chạy với dự án đã lưu trữ, và mở lại
 *   dự án là nó chạy tiếp. Dự án ngoài **vẫn bị giữ**.
 * - `disconnect`: ngắt mềm. Vẫn giữ dự án ngoài để nối lại không tạo trùng.
 * - `release`: ngắt và **giải phóng** — dự án ngoài nối được sang dự án khác.
 */
export const ArchiveSyncChoice = z.enum(['keep', 'disconnect', 'release']);
/**
 * Những đường nối ra ngoài cần ngắt **cùng lúc** với lưu trữ — APM-PARCH-c.
 *
 * Mặc định là không ngắt gì: lưu trữ mở lại được, còn ba trong bốn thứ dưới đây
 * thì không tự quay về (link đã thu hồi phải tạo lại, khách đã gỡ phải mời lại).
 */
export const ArchiveDisconnectInput = z.object({
    sync: ArchiveSyncChoice.default('keep'),
    /** Thu hồi link chia sẻ công khai còn hiệu lực của Sổ chốt phương án. */
    shareLinks: z.boolean().default(false),
    /** Thu hồi link mời còn hiệu lực: thành viên, khách (cộng tác viên), khách hàng. */
    inviteLinks: z.boolean().default(false),
    /** Gỡ khách (GUEST) khỏi dự án — trừ người còn việc chưa xong. */
    guests: z.boolean().default(false),
});
export const ArchiveProjectInput = z.object({
    reason: ProjectArchiveReason,
    /** Ghi chú thêm. Tuỳ chọn: lý do đã chọn thường đủ, và bắt buộc gõ thì người ta gõ "." cho xong. */
    note: z.string().trim().max(500).nullable().optional(),
    /** Bỏ trống = không ngắt gì (client cũ vẫn lưu trữ được như trước). */
    disconnect: ArchiveDisconnectInput.optional(),
});
/**
 * Dự án này đang nối ra ngoài bằng những gì — hộp lưu trữ đọc để chỉ hỏi về thứ
 * **có thật** (APM-PARCH-c). Một ô "thu hồi link chia sẻ" ở dự án không có link
 * nào là một câu hỏi người dùng phải đọc mà không có gì để quyết.
 */
export const ProjectArchiveImpact = z.object({
    /** Kết nối còn **giữ** dự án ngoài: đang chạy, tạm dừng, hoặc đã ngắt mềm. */
    syncConnections: z.array(z.object({
        id: z.uuid(),
        providerName: z.string(),
        externalKey: z.string(),
        externalName: z.string(),
        /** `false` = đã ngắt mềm, chỉ còn giữ chỗ. */
        live: z.boolean(),
    })),
    shareLinks: z.number().int().nonnegative(),
    inviteLinks: z.number().int().nonnegative(),
    /** Khách gỡ được ngay. */
    guests: z.number().int().nonnegative(),
    /** Khách còn việc chưa xong — **không** bị gỡ, phải chuyển việc trước. */
    guestsWithOpenWork: z.number().int().nonnegative(),
});
//# sourceMappingURL=project-archive.js.map